import React, { useEffect, useState } from "react"
import { Modal, Button, Spinner, Tabs, Tab, Badge } from "react-bootstrap"
import { printSingleQuestion } from "../../utils/printQuestions"

const QuestionDetailsModal = ({ show, handleClose, question, loading }) => {
  const [prefetched, setPrefetched] = useState({
    question_file: null,
    ms_file: null,
    deps: {},
  })
  const [prefetching, setPrefetching] = useState(false)

  const tryFetchToObjectURL = async (url) => {
    if (!url) return null
    try {
      const resp = await fetch(url, { mode: "cors" })
      if (!resp.ok) throw new Error(`Fetch failed ${resp.status}`)
      const blob = await resp.blob()
      return URL.createObjectURL(blob)
    } catch (err) {
      console.warn("Prefetch failed for", url, err)
      return null
    }
  }

  // Prefetch images
  useEffect(() => {
    let cancelled = false
    const doPrefetch = async () => {
      if (!question)
        return setPrefetched({ question_file: null, ms_file: null, deps: {} })
      setPrefetching(true)

      const next = { question_file: null, ms_file: null, deps: {} }

      const fetchAndSet = async (url) => {
        if (url?.startsWith("blob:")) return url
        return (await tryFetchToObjectURL(url)) || url
      }

      if (question.question_file)
        next.question_file = await fetchAndSet(question.question_file)
      if (question.ms_file) next.ms_file = await fetchAndSet(question.ms_file)

      if (question.dependencies?.length) {
        await Promise.all(
          question.dependencies.map(async (dep, idx) => {
            const key = dep._id ?? `dep_${idx}`
            if (dep.file_name) next.deps[key] = await fetchAndSet(dep.file_name)
          })
        )
      }

      if (!cancelled) setPrefetched(next)
      setPrefetching(false)
    }

    doPrefetch()

    return () => {
      cancelled = true
      if (prefetched.question_file?.startsWith("blob:"))
        URL.revokeObjectURL(prefetched.question_file)
      if (prefetched.ms_file?.startsWith("blob:"))
        URL.revokeObjectURL(prefetched.ms_file)
      Object.values(prefetched.deps).forEach(
        (s) => s?.startsWith?.("blob:") && URL.revokeObjectURL(s)
      )
    }
  }, [question])

  const getDepSrc = (dep) => prefetched.deps[dep._id] ?? dep.file_name ?? ""

  const handleImgError = (ev, fallbackUrl) => {
    try {
      if (fallbackUrl && ev?.target?.src !== fallbackUrl)
        ev.target.src = fallbackUrl
    } catch {}
  }

  const handlePrint = async () => {
    await printSingleQuestion(question, prefetched, prefetching)
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size='xl'
      className='common-modal document-preview-modal printable-modal'
      centered
      backdrop='static'
    >
      <Modal.Header
        closeButton
        style={{ backgroundColor: "#f8f9fa", borderBottom: "none" }}
      >
        <Modal.Title
          className='document-preview-title'
          style={{ color: "#344054", fontSize: "1.25rem" }}
        >
          {!loading && question && (
            <>
              {`${question?.question_number && "Q."} ${
                question?.question_number ?? ""
              } ${question?.part ?? ""}`}
              {question?.subpart && ` (${question?.subpart})`}
            </>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{
          maxHeight: "80vh",
          overflowY: "auto",
          backgroundColor: "#f8f9fa",
          padding: "2rem",
        }}
      >
        {loading ? (
          <div className='text-center py-5'>
            <Spinner
              animation='border'
              role='status'
              style={{ color: "#7a5d10" }}
            >
              <span className='visually-hidden'>Loading...</span>
            </Spinner>
          </div>
        ) : question ? (
          <>
            {/* Metadata badges (Screen Display) */}
            <div
              className='d-flex gap-2 mb-3 tabbed-section'
              style={{ flexWrap: "wrap" }}
            >
              <Badge
                bg=''
                style={{
                  backgroundColor: "#7a5d10",
                  color: "white",
                  fontSize: "0.875rem",
                  padding: "0.5em 0.75em",
                }}
              >
                {question.year}
              </Badge>
              <Badge
                bg=''
                style={{
                  backgroundColor: "#667085",
                  color: "white",
                  fontSize: "0.875rem",
                  padding: "0.5em 0.75em",
                }}
              >
                {question.paper}
              </Badge>

              <Badge
                bg=''
                style={{
                  backgroundColor: "#be1a00",
                  color: "white",
                  fontSize: "0.875rem",
                  padding: "0.5em 0.75em",
                }}
              >{`Marks: ${question.marks}`}</Badge>
              <Badge
                bg=''
                style={{
                  backgroundColor: "#be1a00",
                  color: "white",
                  fontSize: "0.875rem",
                  padding: "0.5em 0.75em",
                }}
              >{`Order: ${question.order}`}</Badge>
              <Badge
                bg=''
                style={{
                  backgroundColor: "#4b5563",
                  color: "white",
                  fontSize: "0.875rem",
                  padding: "0.5em 0.75em",
                }}
              >{`Section: ${question.section}`}</Badge>
            </div>

            <div className='mb-4 tabbed-section'>
              <h6 style={{ color: "#344054", fontWeight: "600" }}>Topics</h6>
              <div className='d-flex flex-wrap gap-2 mt-2'>
                {question.topics?.map((t) => (
                  <Badge
                    key={t._id}
                    bg=''
                    text='dark'
                    style={{
                      // backgroundColor: "#08AD68",
                      // color: "white",
                      fontSize: "0.875rem",
                      padding: "0.5em 0.75em",
                    }}
                  >
                    {t.title}
                  </Badge>
                ))}
              </div>
            </div>
            <div className='mb-4 tabbed-section'>
              <h6 style={{ color: "#344054", fontWeight: "600" }}>Subtopics</h6>
              <div className='d-flex flex-wrap gap-2 mt-2'>
                {question.subtopics?.map((st) => (
                  <Badge
                    key={st._id}
                    pill
                    bg='light'
                    text='dark'
                    style={{ fontSize: "0.875rem", padding: "0.5em 0.75em" }}
                  >
                    {st.title}
                  </Badge>
                ))}
              </div>
            </div>

            <div className='tabbed-section'>
              <Tabs
                defaultActiveKey='question'
                id='question-details-tabs'
                className='mb-3'
              >
                <Tab eventKey='question' title='Question'>
                  <img
                    src={prefetched.question_file ?? question.question_file}
                    alt='Question'
                    style={{
                      width: "100%",
                      borderRadius: "0.375rem",
                      border: "1px solid #dee2e6",
                    }}
                    onError={(e) => handleImgError(e, question.question_file)}
                  />
                </Tab>
                <Tab eventKey='marking-scheme' title='Marking Scheme'>
                  <img
                    src={
                      prefetched.ms_file ||
                      question.ms_file ||
                      "https://placehold.co/600x400?text=No+image"
                    }
                    alt='Marking Scheme'
                    style={{
                      width: "100%",
                      borderRadius: "0.375rem",
                      border: "1px solid #dee2e6",
                    }}
                    onError={(e) =>
                      handleImgError(
                        e,
                        "https://placehold.co/600x400?text=No+image"
                      )
                    }
                  />
                </Tab>
                {question.dependencies?.length > 0 && (
                  <Tab eventKey='dependencies' title='Dependencies'>
                    {question.dependencies.map((dep, idx) => (
                      <div
                        key={dep._id ?? idx}
                        style={{ marginBottom: "1rem" }}
                      >
                        <img
                          src={getDepSrc(dep)}
                          alt={`Dependency ${idx + 1}`}
                          style={{
                            width: "100%",
                            borderRadius: "0.375rem",
                            border: "1px solid #dee2e6",
                          }}
                          onError={(e) => handleImgError(e, dep.file_name)}
                        />
                      </div>
                    ))}
                  </Tab>
                )}
              </Tabs>
            </div>
          </>
        ) : (
          <p className='text-center py-5'>No question data available.</p>
        )}
      </Modal.Body>

      <Modal.Footer style={{ backgroundColor: "#f8f9fa", borderTop: "none" }}>
        <Button
          variant='light'
          onClick={handleClose}
          style={{ color: "#344054", borderColor: "#d0d5dd" }}
        >
          Close
        </Button>
        <Button variant='primary' onClick={handlePrint} disabled={prefetching}>
          {prefetching ? (
            <>
              <Spinner
                as='span'
                animation='border'
                size='sm'
                role='status'
                aria-hidden='true'
              />{" "}
              Preparing...
            </>
          ) : (
            "Print to PDF"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default QuestionDetailsModal
